import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { BehaviorSubject, filter, take } from 'rxjs';

// === Unified Document Store Filter Types ===
export type ViewTab = 'all' | 'drafts' | 'sales' | 'purchases' | 'warehouse' | 'other' | 'overdue';
export type KsefStatusFilter = 'all' | 'synced' | 'sending' | 'pending' | 'expired' | 'error' | 'not-required';
export type SourceFilter = 'all' | 'local' | 'ksef';
export type PaymentStatusFilter = 'all' | 'undefined' | 'pending' | 'overdue' | 'received';
export type SortField = 'date' | 'amount';
export type SortDirection = 'asc' | 'desc';

// Filtry po adnotacjach faktury
export type AnnotationFilterKey = 'isSelfInvoicing' | 'isSplitPayment' | 'isReverseCharge' | 'isCashMethod' | 'hasMarginProcedure' | 'hasVatExemption';

export interface SortOption {
    field: SortField;
    direction: SortDirection;
}

export interface DocumentFilters {
    view: ViewTab;
    source: SourceFilter;
    ksefStatus: KsefStatusFilter;
    paymentStatus: PaymentStatusFilter;
    paymentMethods: number[];  // Filtry po metodach płatności (ksefValue z AppData.PaymentMethodsSimplified)
    documentTypes: string[];
    annotations: AnnotationFilterKey[];  // Filtry po adnotacjach
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sort: SortOption;
    page: number;
}

export interface DocumentStats {
    all: number;
    drafts: number;
    sales: number;
    purchases: number;
    warehouse: number;
    other: number;
    overdue: number;
    pendingKsef: number;
}

// Domyślne wartości filtrów
const DEFAULT_FILTERS: DocumentFilters = {
    view: 'all',
    source: 'all',
    ksefStatus: 'all',
    paymentStatus: 'all',
    paymentMethods: [],
    documentTypes: [],
    annotations: [],
    sort: { field: 'date', direction: 'desc' },
    page: 1
};

// Dozwolone wartości dla filtrów adnotacji
const VALID_ANNOTATIONS: AnnotationFilterKey[] = [
    'isSelfInvoicing',
    'isSplitPayment', 
    'isReverseCharge',
    'isCashMethod',
    'hasMarginProcedure',
    'hasVatExemption'
];

// Dozwolone wartości dla walidacji query params
const VALID_VIEWS: ViewTab[] = ['all', 'drafts', 'sales', 'purchases', 'warehouse', 'other', 'overdue'];
const VALID_SOURCES: SourceFilter[] = ['all', 'local', 'ksef'];
const VALID_KSEF_STATUSES: KsefStatusFilter[] = ['all', 'synced', 'sending', 'pending', 'expired', 'error', 'not-required'];
const VALID_PAYMENT_STATUSES: PaymentStatusFilter[] = ['all', 'undefined', 'pending', 'overdue', 'received'];
const VALID_SORT_FIELDS: SortField[] = ['date', 'amount'];
const VALID_SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

// Typy dokumentów dla poszczególnych widoków
export const VIEW_DOCUMENT_TYPES: Record<ViewTab, string[]> = {
    all: [],
    drafts: [], // Szkice - wszystkie typy
    sales: [
        'Faktura', 'FakturaVAT', // Faktura VAT (oba warianty nazewnictwa)
        'FakturaKorygujaca', 'FakturaKorygujacaZaliczkowa', 'FakturaKorygujacaRozliczeniowa',
        'FakturaZaliczkowa', 'FakturaZaliczka', // Zaliczkowa (oba warianty)
        'FakturaKoncowa', 'FakturaRozliczeniowa',
        'FakturaMarza', 'FakturaVATMarza', // Marża (oba warianty)
        'Proforma', 'FakturaProforma' // Proforma (oba warianty)
    ],
    purchases: ['Faktura', 'FakturaVAT', 'FakturaKorygujaca', 'FakturaKorygujacaZaliczkowa', 'FakturaKorygujacaRozliczeniowa', 'FakturaZaliczkowa', 'FakturaZaliczka'],
    warehouse: ['WZ', 'PZ', 'MM'],
    other: ['Zamowienie', 'Oferta'],
    overdue: [] // Po terminie płatności - wszystkie typy z NOT_RECEIVED i minięty termin
};

// Typy dokumentów niewymagające KSeF
export const NON_KSEF_DOCUMENT_TYPES = ['Proforma', 'WZ', 'PZ', 'MM', 'Zamowienie', 'Oferta'];

@Injectable({ providedIn: 'root' })
export class DocumentsFilterService {
    
    private _filters$ = new BehaviorSubject<DocumentFilters>({ ...DEFAULT_FILTERS });
    private _stats$ = new BehaviorSubject<DocumentStats>({
        all: 0,
        drafts: 0,
        sales: 0,
        purchases: 0,
        warehouse: 0,
        other: 0,
        overdue: 0,
        pendingKsef: 0
    });
    
    private _syncWithUrl = false;
    
    constructor(private _router: Router) {}
    
    /** Observable z aktualnymi filtrami */
    public filters$ = this._filters$.asObservable();
    
    /** Observable ze statystykami dokumentów */
    public stats$ = this._stats$.asObservable();
    
    /** Pobierz aktualne filtry (snapshot) */
    get currentFilters(): DocumentFilters {
        return this._filters$.getValue();
    }
    
    /** Pobierz aktualne statystyki (snapshot) */
    get currentStats(): DocumentStats {
        return this._stats$.getValue();
    }
    
    /**
     * Inicjalizuje filtry z query params (wywołaj w ngOnInit komponentu listy)
     */
    initFromQueryParams(route: ActivatedRoute): void {
        this._syncWithUrl = true;
        
        route.queryParams.pipe(take(1)).subscribe(params => {
            const filters = this.parseQueryParams(params);
            this._filters$.next(filters);
        });
    }
    
    /**
     * Parsuje query params na filtry
     */
    private parseQueryParams(params: Params): DocumentFilters {
        const filters: DocumentFilters = { ...DEFAULT_FILTERS };
        
        // View
        if (params['view'] && VALID_VIEWS.includes(params['view'])) {
            filters.view = params['view'] as ViewTab;
        }
        
        // Source
        if (params['source'] && VALID_SOURCES.includes(params['source'])) {
            filters.source = params['source'] as SourceFilter;
        }
        
        // KSeF Status
        if (params['ksef'] && VALID_KSEF_STATUSES.includes(params['ksef'])) {
            filters.ksefStatus = params['ksef'] as KsefStatusFilter;
        }
        
        // Payment Status
        if (params['payment'] && VALID_PAYMENT_STATUSES.includes(params['payment'])) {
            filters.paymentStatus = params['payment'] as PaymentStatusFilter;
        }
        
        // Document types (comma-separated)
        if (params['types']) {
            filters.documentTypes = params['types'].split(',').filter((t: string) => t.trim());
        }
        
        // Payment methods (comma-separated ksefValues)
        if (params['pmethods']) {
            filters.paymentMethods = params['pmethods'].split(',').map((v: string) => parseInt(v, 10)).filter((v: number) => !isNaN(v));
        }
        
        // Date range
        if (params['from']) {
            filters.dateFrom = params['from'];
        }
        if (params['to']) {
            filters.dateTo = params['to'];
        }
        
        // Search
        if (params['q']) {
            filters.search = params['q'];
        }
        
        // Annotations (comma-separated)
        if (params['annotations']) {
            const annotationValues = params['annotations'].split(',').filter((a: string) => a.trim());
            filters.annotations = annotationValues.filter((a: string) => 
                VALID_ANNOTATIONS.includes(a as AnnotationFilterKey)
            ) as AnnotationFilterKey[];
        }
        
        // Sort
        if (params['sort'] && VALID_SORT_FIELDS.includes(params['sort'])) {
            filters.sort.field = params['sort'] as SortField;
        }
        if (params['order'] && VALID_SORT_DIRECTIONS.includes(params['order'])) {
            filters.sort.direction = params['order'] as SortDirection;
        }
        
        // Page
        if (params['page']) {
            const pageNum = parseInt(params['page'], 10);
            if (!isNaN(pageNum) && pageNum >= 1) {
                filters.page = pageNum;
            }
        }
        
        return filters;
    }
    
    /**
     * Konwertuje filtry na query params
     */
    private filtersToQueryParams(filters: DocumentFilters): Params {
        const params: Params = {};
        
        // Tylko nie-domyślne wartości
        if (filters.view !== 'all') {
            params['view'] = filters.view;
        }
        if (filters.source !== 'all') {
            params['source'] = filters.source;
        }
        if (filters.ksefStatus !== 'all') {
            params['ksef'] = filters.ksefStatus;
        }
        if (filters.paymentStatus !== 'all') {
            params['payment'] = filters.paymentStatus;
        }
        if (filters.documentTypes && filters.documentTypes.length > 0) {
            params['types'] = filters.documentTypes.join(',');
        }
        if (filters.paymentMethods && filters.paymentMethods.length > 0) {
            params['pmethods'] = filters.paymentMethods.join(',');
        }
        if (filters.annotations && filters.annotations.length > 0) {
            params['annotations'] = filters.annotations.join(',');
        }
        if (filters.dateFrom) {
            params['from'] = filters.dateFrom;
        }
        if (filters.dateTo) {
            params['to'] = filters.dateTo;
        }
        if (filters.search) {
            params['q'] = filters.search;
        }
        
        // Sort (tylko nie-domyślne)
        if (filters.sort.field !== 'date') {
            params['sort'] = filters.sort.field;
        }
        if (filters.sort.direction !== 'desc') {
            params['order'] = filters.sort.direction;
        }
        
        // Page (tylko nie-domyślne)
        if (filters.page > 1) {
            params['page'] = filters.page.toString();
        }
        
        return params;
    }
    
    /**
     * Aktualizuje URL z aktualnymi filtrami
     */
    private updateUrl(): void {
        if (!this._syncWithUrl) return;
        
        const params = this.filtersToQueryParams(this.currentFilters);
        
        this._router.navigate([], {
            queryParams: params,
            queryParamsHandling: '', // Replace all params
            replaceUrl: true // Nie dodawaj do historii przeglądarki
        });
    }
    
    /**
     * Ustawia aktywny widok (tab)
     */
    setView(view: ViewTab): void {
        this.updateFilters({ view });
    }
    
    /**
     * Aktualizuje filtry (partial update)
     * Resetuje stronę do 1 gdy zmieniamy inne filtry (nie page)
     */
    updateFilters(partial: Partial<DocumentFilters>): void {
        // Jeśli zmieniamy cokolwiek innego niż page, resetuj page do 1
        const isPageOnlyChange = Object.keys(partial).length === 1 && 'page' in partial;
        
        this._filters$.next({
            ...this.currentFilters,
            ...partial,
            // Reset page to 1 when other filters change (unless it's a page-only change)
            page: isPageOnlyChange ? (partial.page ?? 1) : 1
        });
        this.updateUrl();
    }
    
    /**
     * Resetuje filtry do wartości domyślnych
     */
    resetFilters(): void {
        this._filters$.next({ ...DEFAULT_FILTERS });
        this.updateUrl();
    }
    
    /**
     * Resetuje filtry zachowując aktualny widok
     */
    resetFiltersKeepView(): void {
        const currentView = this.currentFilters.view;
        this._filters$.next({ 
            ...DEFAULT_FILTERS,
            view: currentView
        });
        this.updateUrl();
    }
    
    /**
     * Wyłącza synchronizację z URL (np. przy opuszczaniu strony)
     */
    disableUrlSync(): void {
        this._syncWithUrl = false;
    }
    
    /**
     * Aktualizuje statystyki dokumentów
     */
    updateStats(stats: DocumentStats): void {
        this._stats$.next(stats);
    }
    
    /**
     * Sprawdza czy są aktywne filtry (poza widokiem)
     */
    hasActiveFilters(): boolean {
        const f = this.currentFilters;
        return !!(
            f.search ||
            f.source !== 'all' ||
            f.ksefStatus !== 'all' ||
            f.paymentStatus !== 'all' ||
            f.dateFrom ||
            f.dateTo ||
            (f.paymentMethods && f.paymentMethods.length > 0) ||
            (f.documentTypes && f.documentTypes.length > 0) ||
            (f.annotations && f.annotations.length > 0)
        );
    }
    
    /**
     * Zwraca liczbę aktywnych filtrów
     */
    getActiveFiltersCount(): number {
        const f = this.currentFilters;
        let count = 0;
        
        if (f.search) count++;
        if (f.source !== 'all') count++;
        if (f.ksefStatus !== 'all') count++;
        if (f.paymentStatus !== 'all') count++;
        if (f.dateFrom) count++;
        if (f.dateTo) count++;
        if (f.paymentMethods && f.paymentMethods.length > 0) count++;
        if (f.documentTypes && f.documentTypes.length > 0) count++;
        if (f.annotations && f.annotations.length > 0) count += f.annotations.length;
        
        return count;
    }
}
